import { useEffect, useState } from "react"
import {
  CAMPAIGN_ACTION_PLAN_STATUS,
  campaignRecommendationExpiresAt,
  type CampaignActionPlan,
  type CampaignPlanSubject,
  TERMINAL_CAMPAIGN_ACTION_PLAN_STATUSES,
  type MarketplaceId,
} from "@nova-ai/shared"
import {
  ArrowRight,
  BarChart3,
  ChevronDown,
  ExternalLink,
  LockKeyhole,
  ShieldCheck,
  X,
} from "lucide-react"

import { useAuth } from "../../hooks/use-auth"
import {
  type CampaignAnalysisDetail,
  useApproveCampaignAction,
  useCampaignActionPlans,
  useStageCampaignAction,
} from "../../services/use-dashboard"
import {
  useCampaignActionBridge,
  type CampaignActionBridgeDispatch,
} from "../../services/campaign-action-bridge"
import { Button } from "../ui/button"
import { Modal } from "../ui/modal"
import { StatusBadge } from "../ui/status-badge"

export type CampaignDrawerSubject = {
  subject: Extract<CampaignPlanSubject, { kind: "existing-campaign" }>
  name: string
  sku: string
  imageUrl: string | null
  productUrl: string | null
  campaignName?: string | null
  campaignId?: string | null
  ean?: string | null
  analysis: CampaignAnalysisDetail | null
}

export function CampaignActionDrawer({
  marketplace,
  campaignActionsEnabled,
  campaignActionsPreview,
  campaignActionTimeZone,
  item,
  onClose,
}: {
  marketplace: MarketplaceId
  campaignActionsEnabled: boolean
  campaignActionsPreview: boolean
  campaignActionTimeZone: string
  item: CampaignDrawerSubject
  onClose: () => void
}) {
  const auth = useAuth()
  const plans = useCampaignActionPlans(marketplace, item.subject, campaignActionsEnabled)
  const stage = useStageCampaignAction(marketplace)
  const approve = useApproveCampaignAction(marketplace)
  const isAdminUser = auth.user?.role === "admin"
  const bridgeEnabled = campaignActionsEnabled && !campaignActionsPreview && isAdminUser
  const bridge = useCampaignActionBridge(marketplace, bridgeEnabled)
  const [manualReason, setManualReason] = useState("")
  const [manualBid, setManualBid] = useState("")
  const subjectPlans =
    plans.data?.plans.filter((candidate) => sameCampaignSubject(candidate.subject, item.subject)) ??
    []
  const authority = campaignDrawerAuthority({
    data: plans.data,
    pending: plans.isPending,
    error: plans.error?.message,
    subject: item.subject,
    campaignActionsEnabled,
    isAdmin: isAdminUser,
  })
  const { latestAnalysis, analysis, analysisReadOnly, readState, canStage, canApprove, isAdmin } =
    authority
  const { activePlan, stagedPlan, plan } = selectCampaignPlanView(
    subjectPlans,
    stage.data,
    item.subject,
    latestAnalysis
  )
  const planStaleReason = plan ? awaitingPlanInvalidReason(plan, latestAnalysis) : null
  const planUsesDirectBridge = campaignPlanUsesDirectBridge(plan, bridge.ready, bridge.planDispatch)
  const [selectedActionIds, setSelectedActionIds] = useState<string[]>([])
  useEffect(() => {
    setSelectedActionIds(plan?.actions.map((action) => action.actionId) ?? [])
  }, [plan])

  const approvePlan = async () => {
    const shouldDispatch = bridgeEnabled
    if (shouldDispatch && !(await bridge.connect())) {
      return
    }
    submitCampaignApproval(plan, selectedActionIds, manualReason, (input) =>
      approve.mutate(input, {
        onSuccess: (approved) => {
          if (shouldDispatch && approved.subject.kind === "existing-campaign") {
            void bridge.dispatch(approved.id, "execute")
          }
        },
      })
    )
  }

  const stagePlan = async (body: Parameters<typeof stage.mutate>[0]) => {
    const shouldDispatch = bridgeEnabled
    if (shouldDispatch && !(await bridge.connect())) {
      return
    }
    stage.mutate(body, {
      onSuccess: (staged) => {
        if (shouldDispatch && staged.subject.kind === "existing-campaign") {
          void bridge.dispatch(staged.id, "prepare")
        }
      },
    })
  }

  return (
    <Modal
      label={`${item.name} campaign details`}
      onClose={onClose}
      backdropClassName="motion-drawer-backdrop bg-black/50 backdrop-blur-[2px]"
      shellClassName="p-0 sm:p-6"
      surfaceClassName="motion-drawer-panel ml-auto h-full max-w-none rounded-none border-0 bg-background sm:max-w-[34rem] sm:rounded-xl sm:border"
    >
      <DrawerHeader item={item} onClose={onClose} />

      <DrawerContent
        analysis={analysis}
        analysisReadOnly={analysisReadOnly}
        plan={plan}
        planStaleReason={planStaleReason}
        hasActivePlan={activePlan !== null || stagedPlan !== null}
        plans={subjectPlans}
        campaignActionTimeZone={campaignActionTimeZone}
        campaignActionsEnabled={campaignActionsEnabled}
        campaignActionsPreview={campaignActionsPreview}
        campaignActionsLocal={bridgeEnabled}
        campaignBridgeError={bridge.error}
        campaignPlanDirect={planUsesDirectBridge}
        isAdmin={isAdmin}
        canStage={canStage}
        canApprove={canApprove}
        stagePending={stage.isPending || bridge.connecting}
        stageError={stage.error?.message ?? null}
        planReadAuthoritative={readState.authoritative}
        planReadPending={readState.pending}
        planReadError={readState.error}
        approvePending={approve.isPending || bridge.connecting}
        approveError={approve.error?.message ?? null}
        selectedActionIds={selectedActionIds}
        onSelectedActionIdsChange={setSelectedActionIds}
        manualReason={manualReason}
        onManualReasonChange={setManualReason}
        manualBid={manualBid}
        onManualBidChange={setManualBid}
        onStageAi={() => void stagePlan(campaignStageBody(item.subject, "ai"))}
        onStageManual={() => void stagePlan(campaignStageBody(item.subject, "manual"))}
        onStageBid={() => {
          const bidCents = parseBidCents(manualBid)
          if (bidCents !== null) {
            void stagePlan({ subject: item.subject, intent: { kind: "manual-bid", bidCents } })
          }
        }}
        onApprove={() => void approvePlan()}
      />
    </Modal>
  )
}

function campaignDrawerAuthority(input: {
  data: ReturnType<typeof useCampaignActionPlans>["data"]
  pending: boolean
  error: string | undefined
  subject: CampaignPlanSubject
  campaignActionsEnabled: boolean
  isAdmin: boolean
}) {
  const latestAnalysis = input.data?.latestAnalysis ?? null
  const analysisAuthority = campaignAnalysisAuthority(
    input.subject,
    latestAnalysis,
    input.data?.latestAnalysisDetail ?? null
  )
  const readState = campaignPlanReadState({
    hasData: input.data !== undefined,
    pending: input.pending,
    error: input.error ?? null,
  })
  return {
    latestAnalysis,
    analysis: analysisAuthority.readable,
    analysisReadOnly:
      readState.authoritative &&
      analysisAuthority.readable !== null &&
      analysisAuthority.actionable === null,
    readState,
    isAdmin: input.isAdmin,
    ...campaignDrawerPermissions(
      input.campaignActionsEnabled,
      readState.authoritative && analysisAuthority.actionable !== null,
      input.isAdmin
    ),
  }
}

export function campaignAnalysisAuthority(
  subject: CampaignPlanSubject,
  identity: AnalysisIdentity,
  detail: CampaignAnalysisDetail | null
) {
  const readable = matchingSubjectAnalysis(subject, detail)
  return {
    readable,
    actionable: matchingLatestAnalysis(subject, identity, detail),
  }
}

function matchingSubjectAnalysis(
  subject: CampaignPlanSubject,
  detail: CampaignAnalysisDetail | null
): CampaignAnalysisDetail | null {
  if (
    detail?.productId !== subject.productId ||
    (subject.kind === "existing-campaign" && detail.campaignId !== subject.campaignId)
  ) {
    return null
  }
  return detail
}

export function matchingLatestAnalysis(
  subject: CampaignPlanSubject,
  identity: AnalysisIdentity,
  detail: CampaignAnalysisDetail | null
): CampaignAnalysisDetail | null {
  const matchingDetail = matchingSubjectAnalysis(subject, detail)
  if (
    matchingDetail === null ||
    matchingDetail.runId !== identity?.runId ||
    matchingDetail.rowPosition !== identity?.rowPosition
  ) {
    return null
  }
  return matchingDetail
}

function submitCampaignApproval(
  plan: CampaignActionPlan | null,
  selectedActionIds: string[],
  manualReason: string,
  mutate: (input: {
    planId: string
    body: {
      fingerprint: string
      selectedActionIds: string[]
      manualOverrideReason: string | null
    }
  }) => void
) {
  if (!plan?.stagingFingerprint) {
    return
  }
  mutate({
    planId: plan.id,
    body: {
      fingerprint: plan.stagingFingerprint,
      selectedActionIds,
      manualOverrideReason: selectedManualOverrideReason(plan, selectedActionIds, manualReason),
    },
  })
}

export function campaignDrawerPermissions(
  campaignActionsEnabled: boolean,
  authoritative: boolean,
  isAdmin: boolean
) {
  return {
    canStage: isAdmin && campaignActionsEnabled && authoritative,
    canApprove: isAdmin && campaignActionsEnabled && authoritative,
  }
}

function campaignStageBody(
  subject: Extract<CampaignPlanSubject, { kind: "existing-campaign" }>,
  intent: "ai" | "manual"
) {
  return {
    subject,
    intent:
      intent === "ai"
        ? ({ kind: "ai-recommendation" } as const)
        : ({ kind: "manual-opposite" } as const),
  }
}

function selectedManualOverrideReason(
  plan: CampaignActionPlan,
  selectedActionIds: string[],
  manualReason: string
): string | null {
  const selectedManualOverride = plan.actions.some(
    (action) => selectedActionIds.includes(action.actionId) && action.manualOverride
  )
  return selectedManualOverride ? manualReason.trim() : null
}

type PlanViewCandidate = Pick<
  CampaignActionPlan,
  "status" | "subject" | "expiresAt" | "analysisRunId" | "analysisRowPosition"
>

type AnalysisIdentity = { runId: string; rowPosition: number } | null

export function selectCampaignPlanView<T extends PlanViewCandidate>(
  subjectPlans: T[],
  stagedCandidate: T | undefined,
  subject: CampaignPlanSubject,
  latestAnalysis: AnalysisIdentity,
  now = Date.now()
) {
  const activePlan =
    subjectPlans.find(
      (candidate) =>
        !TERMINAL_CAMPAIGN_ACTION_PLAN_STATUSES.has(candidate.status) &&
        awaitingPlanInvalidReason(candidate, latestAnalysis, now) === null
    ) ?? null
  const stagedPlan =
    subjectPlans.length === 0 &&
    stagedCandidate &&
    sameCampaignSubject(stagedCandidate.subject, subject) &&
    !TERMINAL_CAMPAIGN_ACTION_PLAN_STATUSES.has(stagedCandidate.status) &&
    awaitingPlanInvalidReason(stagedCandidate, latestAnalysis, now) === null
      ? stagedCandidate
      : null
  return {
    activePlan,
    stagedPlan,
    plan: activePlan ?? subjectPlans[0] ?? stagedPlan ?? null,
  }
}

export function awaitingPlanInvalidReason(
  plan: PlanViewCandidate,
  latestAnalysis: AnalysisIdentity,
  now = Date.now()
): string | null {
  if (plan.status !== CAMPAIGN_ACTION_PLAN_STATUS.awaitingApproval) {
    return null
  }
  if (plan.expiresAt === null || Date.parse(plan.expiresAt) <= now) {
    return "This staged plan snapshot expired. Stage a fresh plan."
  }
  if (
    plan.analysisRunId !== latestAnalysis?.runId ||
    plan.analysisRowPosition !== latestAnalysis?.rowPosition
  ) {
    return "A newer completed analysis replaced this approval. Stage a fresh plan."
  }
  return null
}

export function campaignPlanReadState(input: {
  hasData: boolean
  pending: boolean
  error: string | null
}): { authoritative: boolean; pending: boolean; error: string | null } {
  return {
    authoritative: input.hasData && input.error === null,
    pending: !input.hasData && input.pending,
    error: input.error,
  }
}

export function CampaignPlanReadNotice({
  authoritative,
  pending,
  error,
}: {
  authoritative: boolean
  pending: boolean
  error: string | null
}) {
  if (error) {
    return <ErrorText message={`Campaign action state could not be refreshed: ${error}`} />
  }
  if (pending) {
    return <p className="mt-3 text-sm text-muted-foreground">Reading durable action state…</p>
  }
  if (!authoritative) {
    return (
      <p role="status" className="mt-3 text-sm text-muted-foreground">
        Durable campaign action history is unavailable.
      </p>
    )
  }
  return null
}

function DrawerHeader({ item, onClose }: { item: CampaignDrawerSubject; onClose: () => void }) {
  return (
    <header className="flex items-start gap-3 border-b border-border/70 bg-card px-5 py-4 sm:px-6">
      <ProductImage url={item.imageUrl} name={item.name} />

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="truncate font-semibold text-foreground" title={item.name}>
              {item.name}
            </h2>

            <p className="mt-0.5 text-xs text-muted-foreground">SKU {item.sku}</p>

            <SubjectMetadata item={item} />
          </div>

          <Button
            className="-mr-3 size-11 shrink-0 p-0"
            variant="ghost"
            size="sm"
            aria-label="Close campaign details"
            onClick={onClose}
          >
            <X className="size-4" />
          </Button>
        </div>

        {item.productUrl ? (
          <a
            href={item.productUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-foreground underline-offset-4 hover:underline"
          >
            Open product <ExternalLink className="size-3" />
          </a>
        ) : null}
      </div>
    </header>
  )
}

function SubjectMetadata({ item }: { item: CampaignDrawerSubject }) {
  if (item.campaignId) {
    return (
      <p
        className="mt-1 truncate text-xs text-muted-foreground"
        title={`${item.campaignName ?? "Campaign"} · ID ${item.campaignId}`}
      >
        {`${item.campaignName ?? "Campaign"} · ID ${item.campaignId}`}
      </p>
    )
  }
  if (item.ean) {
    return <p className="mt-0.5 text-xs text-muted-foreground">{`EAN ${item.ean}`}</p>
  }
  return null
}

type DrawerContentProps = {
  analysis: CampaignAnalysisDetail | null
  analysisReadOnly: boolean
  plan: CampaignActionPlan | null
  planStaleReason: string | null
  hasActivePlan: boolean
  plans: CampaignActionPlan[]
  campaignActionTimeZone: string
  campaignActionsEnabled: boolean
  campaignActionsPreview: boolean
  campaignActionsLocal: boolean
  campaignBridgeError: string | null
  campaignPlanDirect: boolean
  isAdmin: boolean
  canApprove: boolean
  canStage: boolean
  stagePending: boolean
  stageError: string | null
  planReadAuthoritative: boolean
  planReadPending: boolean
  planReadError: string | null
  approvePending: boolean
  approveError: string | null
  selectedActionIds: string[]
  onSelectedActionIdsChange: (ids: string[]) => void
  manualReason: string
  onManualReasonChange: (value: string) => void
  manualBid: string
  onManualBidChange: (value: string) => void
  onStageAi: () => void
  onStageManual: () => void
  onStageBid: () => void
  onApprove: () => void
}

function DrawerContent(props: DrawerContentProps) {
  return (
    <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6 sm:py-6">
      {props.analysis ? <AnalysisSection analysis={props.analysis} /> : <NoAnalysis />}

      <ActionPlanSection {...props} />

      <EarlierAttempts plans={props.plans} displayedPlanId={props.plan?.id ?? null} />
    </div>
  )
}

function ActionPlanSection(props: DrawerContentProps) {
  const awaitingApproval =
    props.plan?.status === CAMPAIGN_ACTION_PLAN_STATUS.awaitingApproval &&
    props.planStaleReason === null &&
    props.planReadError === null
  if (
    shouldShowLockedActionsNotice({
      campaignActionsEnabled: props.campaignActionsEnabled,
      hasAnalysis: props.analysis !== null,
      hasPlan: props.plan !== null,
      planReadAuthoritative: props.planReadAuthoritative,
    })
  ) {
    return <LockedActionsNotice />
  }
  return (
    <section className="mt-6 border-t border-border/70 pt-5" aria-labelledby="action-plan">
      <div className="flex items-center justify-between gap-3">
        <h3 id="action-plan" className="text-sm font-semibold text-foreground">
          Protected action plan
        </h3>

        {props.plan ? <PlanStatus plan={props.plan} /> : null}
      </div>

      <CapabilityNotice
        enabled={props.campaignActionsEnabled}
        preview={props.campaignActionsPreview}
        isAdmin={props.isAdmin}
        error={props.campaignBridgeError}
      />

      <CampaignPlanReadNotice
        authoritative={props.planReadAuthoritative}
        pending={props.planReadPending}
        error={props.planReadError}
      />

      <AnalysisReadOnlyNotice visible={props.analysisReadOnly} />

      {props.planStaleReason ? <ErrorText message={props.planStaleReason} /> : null}

      {props.plan ? (
        <PlanReview
          plan={props.plan}
          preview={props.campaignActionsPreview}
          direct={props.campaignPlanDirect}
          selectionEnabled={awaitingApproval}
          selectedActionIds={props.selectedActionIds}
          onSelectedActionIdsChange={props.onSelectedActionIdsChange}
        />
      ) : null}

      <StageControls {...props} />

      {props.stageError ? <ErrorText message={props.stageError} /> : null}

      {awaitingApproval && props.plan ? (
        <ApprovalControls
          plan={props.plan}
          timeZone={props.campaignActionTimeZone}
          enabled={props.canApprove}
          selectedActionIds={props.selectedActionIds}
          manualReason={props.manualReason}
          onReasonChange={props.onManualReasonChange}
          pending={props.approvePending}
          preview={props.campaignActionsPreview}
          direct={props.campaignActionsLocal}
          onApprove={props.onApprove}
        />
      ) : null}

      {props.approveError ? <ErrorText message={props.approveError} /> : null}
    </section>
  )
}

export function shouldShowLockedActionsNotice(input: {
  campaignActionsEnabled: boolean
  hasAnalysis: boolean
  hasPlan: boolean
  planReadAuthoritative: boolean
}) {
  return (
    !input.campaignActionsEnabled &&
    input.hasAnalysis &&
    !input.hasPlan &&
    input.planReadAuthoritative
  )
}

export function LockedActionsNotice() {
  return (
    <section className="mt-4 rounded-xl bg-card p-4 shadow-card" aria-labelledby="actions-locked">
      <div className="flex gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-warning/10 text-warning">
          <LockKeyhole className="size-4" aria-hidden="true" />
        </span>

        <div>
          <h3 id="actions-locked" className="text-sm font-semibold text-foreground">
            Campaign actions disabled
          </h3>

          <p className="mt-1 text-sm leading-5 text-muted-foreground">
            Campaign changes are disabled for this marketplace. Recommendations remain available for
            review.
          </p>
        </div>
      </div>
    </section>
  )
}

export function AnalysisReadOnlyNotice({ visible }: { visible: boolean }) {
  return visible ? (
    <div
      role="status"
      className="mt-3 rounded-lg border border-border bg-muted/25 p-3 text-sm text-muted-foreground"
    >
      The latest marketplace analysis omitted this campaign. Its last subject analysis remains
      readable, but staging and approval require a new actionable row.
    </div>
  ) : null
}

export function CapabilityNotice({
  enabled,
  preview,
  isAdmin,
  error = null,
}: {
  enabled: boolean
  preview: boolean
  isAdmin: boolean
  error?: string | null
}) {
  if (preview && enabled) {
    return (
      <p className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
        <ShieldCheck className="size-3.5 text-success" aria-hidden="true" />
        Local simulation. Approval updates only the throwaway database; Redcare stays untouched.
      </p>
    )
  }
  if (!enabled) {
    return null
  }
  if (error) {
    return <ErrorText message={error} />
  }
  if (!isAdmin) {
    return (
      <div className="mt-3 rounded-lg border border-border bg-muted/25 p-3 text-sm text-muted-foreground">
        Campaign actions are available only to dashboard admins.
      </div>
    )
  }
  return null
}

function StageControls(props: DrawerContentProps) {
  const recommendationExpiry = props.analysis
    ? campaignRecommendationExpiresAt(props.analysis.completedAt, props.campaignActionTimeZone)
    : null
  const expiry = useExpiryState(recommendationExpiry, props.campaignActionTimeZone)
  if (props.hasActivePlan || props.analysis === null) {
    return null
  }
  return (
    <div className="mt-4 flex flex-wrap gap-2">
      {isActionable(props.analysis.decision) ? (
        <Button
          variant="primary"
          disabled={!props.canStage || props.stagePending || expiry.expired}
          onClick={props.onStageAi}
        >
          {props.stagePending ? "Preparing…" : aiActionLabel(props.analysis)}
        </Button>
      ) : null}

      {isActionable(props.analysis.decision) && recommendationExpiry ? (
        <p className="basis-full text-xs text-muted-foreground">{expiry.label}</p>
      ) : null}

      <ExistingCampaignControls {...props} recommendationExpired={expiry.expired} />
    </div>
  )
}

function ExistingCampaignControls(props: DrawerContentProps & { recommendationExpired: boolean }) {
  if (props.analysis === null) {
    return null
  }
  const showStateAction = shouldShowManualStateAction(props.analysis, props.recommendationExpired)
  return (
    <>
      {showStateAction ? (
        <Button disabled={!props.canStage || props.stagePending} onClick={props.onStageManual}>
          {manualStateActionLabel(props.analysis)}
        </Button>
      ) : null}

      <div className="basis-full pt-2">
        <label className="text-xs font-medium text-foreground" htmlFor="manual-cpc-bid">
          Exact CPC bid
        </label>

        <div className="mt-1.5 flex gap-2">
          <div className="flex flex-1 items-center rounded-lg border border-border bg-card px-3 focus-within:ring-2 focus-within:ring-ring">
            <span className="text-sm text-muted-foreground">€</span>

            <input
              id="manual-cpc-bid"
              inputMode="decimal"
              value={props.manualBid}
              onChange={(event) => props.onManualBidChange(event.target.value)}
              placeholder="0.40"
              className="min-w-0 flex-1 bg-transparent px-1 py-2 text-sm outline-none"
            />
          </div>

          <Button
            disabled={
              !props.canStage || props.stagePending || parseBidCents(props.manualBid) === null
            }
            onClick={props.onStageBid}
          >
            Set bid
          </Button>
        </div>
      </div>
    </>
  )
}

function EarlierAttempts({
  plans,
  displayedPlanId,
}: {
  plans: CampaignActionPlan[]
  displayedPlanId: string | null
}) {
  const earlier = plans.filter((entry) => entry.id !== displayedPlanId)
  if (earlier.length === 0) {
    return null
  }
  return (
    <section className="mt-6 border-t border-border/70 pt-5">
      <h3 className="text-sm font-semibold text-foreground">Earlier attempts</h3>

      <ul className="mt-3 space-y-2 text-xs text-muted-foreground">
        {earlier.map((entry) => (
          <li key={entry.id} className="flex justify-between gap-3">
            <span>{new Date(entry.createdAt).toLocaleString()}</span>

            <span>{statusText(entry.status)}</span>
          </li>
        ))}
      </ul>
    </section>
  )
}

export function AnalysisSection({ analysis }: { analysis: CampaignAnalysisDetail }) {
  const sourceUrl = safeHttpsUrl(analysis.sourceUrl)
  const bidCopy = bidRecommendation(analysis)
  return (
    <section
      className="rounded-xl bg-card p-4 shadow-card sm:p-5"
      aria-labelledby="latest-analysis"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <StatusBadge tone={decisionTone(analysis.decision)}>
            {decisionText(analysis.decision)}
          </StatusBadge>

          <span className="text-xs capitalize text-muted-foreground">
            {analysis.priority} priority
          </span>
        </div>

        <span className="text-xs text-muted-foreground">
          {new Date(analysis.completedAt).toLocaleDateString()}
        </span>
      </div>

      <div className="mt-4">
        <h3 id="latest-analysis" className="text-lg font-semibold text-foreground">
          {recommendationTitle(analysis.decision)}
        </h3>

        {bidCopy ? (
          <p className="mt-1 text-sm tabular-nums text-muted-foreground">{bidCopy}</p>
        ) : null}
      </div>

      <p className="mt-3 text-sm leading-6 text-foreground">{analysis.reasoning}</p>

      <div className="mt-4 flex gap-3 rounded-lg bg-muted/40 p-3">
        <ArrowRight className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />

        <div>
          <p className="text-xs font-medium text-muted-foreground">Next step</p>

          <p className="mt-1 text-sm leading-5 text-foreground">{analysis.nextStep}</p>
        </div>
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Metric label="Spend" value={money(analysis.spend)} />

        <Metric label="ROAS" value={ratio(analysis.roas)} />

        <Metric label="Break-even" value={ratio(analysis.breakEvenRoas)} />

        <Metric label="Conversions" value={number(analysis.conversions)} />
      </dl>

      <details className="group mt-4 border-t border-border/70 pt-4">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
          Review all evidence
          <ChevronDown
            className="size-4 text-muted-foreground transition-transform group-open:rotate-180"
            aria-hidden="true"
          />
        </summary>

        <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <Fact label="Analysed" value={new Date(analysis.completedAt).toLocaleString()} />

          <Fact label="Current bid" value={money(analysis.currentBid)} />

          <Fact label="Recommended bid" value={money(analysis.recommendedBid)} />

          <Fact label="GMV" value={money(analysis.gmv)} />

          <Fact label="Target ROAS" value={ratio(analysis.targetRoas)} />

          <Fact label="Bid ceiling" value={money(analysis.applicableBidCeiling)} />

          <Fact label="Clicks" value={number(analysis.clicks)} />

          <Fact label="Organic units" value={number(analysis.organicUnits)} />

          <Fact label="Organic revenue" value={money(analysis.organicRevenue)} />

          <Fact label="Organic orders" value={number(analysis.organicOrders)} />

          <Fact label="Unit margin" value={money(analysis.unitMargin)} />

          <Fact label="Margin pool" value={money(analysis.marginPool)} />

          <Fact label="Campaign state" value={stateText(analysis.isActive)} />

          <Fact label="Offer state" value={stockText(analysis.soldOut)} />

          <Fact label="Server comparison" value={comparisonText(analysis)} />
        </dl>

        {analysis.gateReasons.length > 0 ? (
          <div className="mt-4 rounded-lg bg-muted/40 p-3">
            <p className="text-xs font-medium text-foreground">Evidence checks</p>

            <ul className="mt-2 space-y-1 text-xs leading-5 text-muted-foreground">
              {analysis.gateReasons.map((reason) => (
                <li key={reason}>• {reason}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {sourceUrl ? (
          <a
            href={sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-foreground underline-offset-4 hover:underline"
          >
            Open source evidence <ExternalLink className="size-3" />
          </a>
        ) : null}
      </details>
    </section>
  )
}

export function PlanReview({
  plan,
  preview,
  direct,
  selectionEnabled,
  selectedActionIds,
  onSelectedActionIdsChange,
}: {
  plan: CampaignActionPlan
  preview: boolean
  direct: boolean
  selectionEnabled: boolean
  selectedActionIds: string[]
  onSelectedActionIdsChange: (ids: string[]) => void
}) {
  if (plan.status === CAMPAIGN_ACTION_PLAN_STATUS.staging) {
    return <p className="mt-3 text-sm text-muted-foreground">Preparing the plan snapshot…</p>
  }
  return (
    <div className="mt-4 space-y-3">
      {direct ? <DirectPlanProgress plan={plan} /> : null}

      {plan.actions.map((action) => (
        <article key={action.actionId} className="rounded-lg border border-border p-3">
          <div className="flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-sm font-medium text-foreground">
              <input
                type="checkbox"
                checked={selectedActionIds.includes(action.actionId)}
                disabled={!selectionEnabled}
                onChange={(event) =>
                  onSelectedActionIdsChange(
                    selectActionWithDependencies(
                      plan.actions,
                      selectedActionIds,
                      action.actionId,
                      event.target.checked
                    )
                  )
                }
              />

              <span>{action.label}</span>
            </label>

            <span className="text-xs text-muted-foreground">
              {action.manualOverride ? "Manual override" : "AI recommendation"}
            </span>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
            <Snapshot label="Before" snapshot={action.before} />

            <Snapshot label="Proposed after" snapshot={action.after} />
          </div>

          <p className="mt-3 text-xs text-muted-foreground">
            Preserved: {action.preservedFields.join(", ") || "none"}
          </p>
        </article>
      ))}

      {plan.resultReason ? (
        <div className="rounded-lg border border-border bg-muted/20 p-3 text-sm text-foreground">
          {plan.resultReason}
        </div>
      ) : null}

      {plan.fingerprint ? (
        <div className="rounded-lg border border-border bg-muted/20 p-3">
          <p className="text-xs font-medium text-foreground">Approval fingerprint</p>

          <p className="mt-1 break-all font-mono text-xs text-muted-foreground">
            {plan.fingerprint}
          </p>
        </div>
      ) : null}

      {TERMINAL_CAMPAIGN_ACTION_PLAN_STATUSES.has(plan.status) ? (
        <div className="rounded-lg border border-border bg-muted/20 p-3">
          <Snapshot label={terminalOutcomeLabel(plan, preview)} snapshot={plan.resultAfter} />

          {plan.evidenceRef ? (
            <p className="mt-2 break-all text-xs text-muted-foreground">
              Evidence: {plan.evidenceRef}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export function campaignPlanUsesDirectBridge(
  plan: Pick<CampaignActionPlan, "id" | "status"> | null,
  bridgeReady: boolean,
  planDispatch: CampaignActionBridgeDispatch | null
): boolean {
  if (!plan) {
    return false
  }
  const matchingDispatch = planDispatch?.planId === plan.id ? planDispatch.phase : null
  if (plan.status === CAMPAIGN_ACTION_PLAN_STATUS.staging) {
    return matchingDispatch === "prepare"
  }
  if (plan.status === CAMPAIGN_ACTION_PLAN_STATUS.awaitingApproval) {
    return bridgeReady
  }
  if (
    plan.status === CAMPAIGN_ACTION_PLAN_STATUS.approved ||
    plan.status === CAMPAIGN_ACTION_PLAN_STATUS.executing
  ) {
    return matchingDispatch === "execute"
  }
  return false
}

function DirectPlanProgress({ plan }: { plan: CampaignActionPlan }) {
  if (plan.status === CAMPAIGN_ACTION_PLAN_STATUS.awaitingApproval) {
    return null
  }
  if (plan.status === CAMPAIGN_ACTION_PLAN_STATUS.approved) {
    return <p className="text-sm text-muted-foreground">Starting the approved action…</p>
  }
  if (plan.status === CAMPAIGN_ACTION_PLAN_STATUS.executing) {
    return <p className="text-sm text-muted-foreground">Applying and verifying in Redcare…</p>
  }
  return null
}

export function terminalOutcomeLabel(plan: CampaignActionPlan, preview: boolean): string {
  if (preview || plan.status === CAMPAIGN_ACTION_PLAN_STATUS.simulated) {
    return "Simulated outcome"
  }
  if (plan.status === CAMPAIGN_ACTION_PLAN_STATUS.appliedAndVerified) {
    return "Applied and verified"
  }
  if (plan.status === CAMPAIGN_ACTION_PLAN_STATUS.alreadyApplied) {
    return "Already applied"
  }
  if (plan.status === CAMPAIGN_ACTION_PLAN_STATUS.failedBeforeClick) {
    return "Failed before click"
  }
  return "Uncertain after possible click"
}

function ApprovalControls({
  plan,
  timeZone,
  enabled,
  selectedActionIds,
  manualReason,
  onReasonChange,
  pending,
  preview,
  direct,
  onApprove,
}: {
  plan: CampaignActionPlan
  timeZone: string
  enabled: boolean
  selectedActionIds: string[]
  manualReason: string
  onReasonChange: (value: string) => void
  pending: boolean
  preview: boolean
  direct: boolean
  onApprove: () => void
}) {
  const expiry = useExpiryState(plan.expiresAt, timeZone)
  const manual = plan.actions.some(
    (action) => selectedActionIds.includes(action.actionId) && action.manualOverride
  )
  return (
    <div className="mt-4 rounded-lg border border-foreground/15 bg-foreground/[0.025] p-4">
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        <ShieldCheck className="size-4" /> Plan freshness fingerprint
      </div>

      <p className="mt-1 break-all font-mono text-xs text-muted-foreground">
        {plan.stagingFingerprint}
      </p>

      <p className="mt-2 text-xs text-muted-foreground">{expiry.label}</p>

      <p className="mt-2 text-xs text-muted-foreground">
        Select actions first. Approval creates a separate single-use fingerprint bound to that
        selection.
      </p>

      {manual ? (
        <label className="mt-3 block text-xs font-medium text-foreground">
          Manual override reason
          <textarea
            value={manualReason}
            onChange={(event) => onReasonChange(event.target.value)}
            className="mt-1.5 min-h-20 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm font-normal outline-none focus:ring-2 focus:ring-ring"
            placeholder="Why is this manual change safer?"
          />
        </label>
      ) : null}

      <Button
        className="mt-3 w-full"
        variant="primary"
        disabled={
          !enabled ||
          pending ||
          selectedActionIds.length === 0 ||
          plan.safetyState !== "eligible" ||
          expiry.expired ||
          (manual && manualReason.trim().length < 4)
        }
        onClick={onApprove}
      >
        {approvalButtonLabel(pending, preview, direct)}
      </Button>
    </div>
  )
}

function approvalButtonLabel(pending: boolean, preview: boolean, direct: boolean) {
  if (pending) {
    return "Approving…"
  }
  if (preview) {
    return "Approve simulation"
  }
  return direct ? "Approve and apply" : "Approve exact plan"
}

export function parseBidCents(value: string): number | null {
  const normalized = value.trim().replace(",", ".")
  if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) {
    return null
  }
  const cents = Math.round(Number(normalized) * 100)
  return Number.isInteger(cents) && cents > 0 ? cents : null
}

function Snapshot({
  label,
  snapshot,
}: {
  label: string
  snapshot: CampaignActionPlan["actions"][number]["before"]
}) {
  if (!snapshot) {
    return (
      <div>
        <p className="font-medium text-muted-foreground">{label}</p>

        <p className="mt-1 text-foreground">No campaign</p>
      </div>
    )
  }
  return (
    <div>
      <p className="font-medium text-muted-foreground">{label}</p>

      <p className="mt-1 text-foreground">{money(snapshot.bidCents / 100)} CPC</p>

      <p className="text-muted-foreground">{snapshot.campaignName}</p>

      <p className="text-muted-foreground">
        {snapshot.campaignId
          ? `Campaign ${snapshot.campaignId}`
          : "Campaign ID assigned after creation"}
      </p>

      <p className="text-muted-foreground">
        €{(snapshot.dailyBudgetCents / 100).toFixed(2)}/day cap
      </p>

      <p className="text-muted-foreground">{snapshot.isActive ? "Active" : "Inactive"}</p>

      <p className="text-muted-foreground">
        {snapshot.soldOut || !snapshot.offerUsable ? "Offer unavailable" : "Offer usable"}
      </p>

      <p className="text-muted-foreground">{`${snapshot.startDate} → ${snapshot.endDate}`}</p>
    </div>
  )
}

function PlanStatus({ plan }: { plan: CampaignActionPlan }) {
  const tone = planStatusTone(plan.status)
  return <StatusBadge tone={tone}>{statusText(plan.status)}</StatusBadge>
}

function planStatusTone(status: CampaignActionPlan["status"]) {
  if (!TERMINAL_CAMPAIGN_ACTION_PLAN_STATUSES.has(status)) {
    return "muted" as const
  }
  if (status === CAMPAIGN_ACTION_PLAN_STATUS.appliedAndVerified) {
    return "success" as const
  }
  return "destructive" as const
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>

      <dd className="mt-0.5 tabular-nums text-foreground">{value}</dd>
    </div>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-muted/40 px-3 py-2.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>

      <dd className="mt-1 font-semibold tabular-nums text-foreground">{value}</dd>
    </div>
  )
}

function ProductImage({ url, name }: { url: string | null; name: string }) {
  return url ? (
    <img
      src={url}
      alt={name}
      className="size-12 shrink-0 rounded-lg border border-border bg-white object-contain"
    />
  ) : (
    <div
      className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-muted/50 text-muted-foreground"
      aria-hidden="true"
    >
      <BarChart3 className="size-5" />
    </div>
  )
}

export function NoAnalysis() {
  return (
    <section className="flex items-start gap-3 py-2" aria-labelledby="no-campaign-analysis">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <BarChart3 className="size-4" aria-hidden="true" />
      </span>

      <div>
        <h3 id="no-campaign-analysis" className="text-sm font-semibold text-foreground">
          No recommendation yet
        </h3>

        <p className="mt-1 text-sm leading-5 text-muted-foreground">
          This campaign has not completed an analysis.
        </p>
      </div>
    </section>
  )
}

function ErrorText({ message }: { message: string }) {
  return (
    <p role="alert" className="mt-3 text-sm text-destructive">
      {message}
    </p>
  )
}

function useExpiryState(expiresAt: string | null, timeZone: string) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (expiresAt === null) {
      return
    }
    const timer = globalThis.setInterval(() => setNow(Date.now()), 1_000)
    return () => globalThis.clearInterval(timer)
  }, [expiresAt])
  return recommendationExpiryState(expiresAt, timeZone, now)
}

export function recommendationExpiryState(
  expiresAt: string | null,
  timeZone: string,
  now = Date.now()
) {
  if (expiresAt === null) {
    return { expired: true, label: "No valid expiry remains. Run a new ads review." }
  }
  const expiry = Date.parse(expiresAt)
  const remaining = expiry - now
  const formatted = new Intl.DateTimeFormat(undefined, {
    timeZone,
    dateStyle: "medium",
    timeStyle: "medium",
  }).format(new Date(expiry))
  if (remaining <= 0) {
    return {
      expired: true,
      label: `Expired ${formatted} (${timeZone}). Run a new ads review to refresh this recommendation.`,
    }
  }
  const minutes = Math.floor(remaining / 60_000)
  const seconds = Math.floor((remaining % 60_000) / 1_000)
  return {
    expired: false,
    label: `Expires in ${minutes}m ${seconds}s at ${formatted} (${timeZone}).`,
  }
}

function selectActionWithDependencies(
  actions: CampaignActionPlan["actions"],
  selectedActionIds: string[],
  actionId: string,
  checked: boolean
): string[] {
  const selected = new Set(selectedActionIds)
  const byId = new Map(actions.map((action) => [action.actionId, action]))
  if (checked) {
    const add = (id: string) => {
      selected.add(id)
      byId.get(id)?.dependsOnActionIds.forEach(add)
    }
    add(actionId)
  } else {
    selected.delete(actionId)
    let changed = true
    while (changed) {
      changed = false
      for (const action of actions) {
        if (
          selected.has(action.actionId) &&
          action.dependsOnActionIds.some((dependency) => !selected.has(dependency))
        ) {
          selected.delete(action.actionId)
          changed = true
        }
      }
    }
  }
  return actions.filter((action) => selected.has(action.actionId)).map((action) => action.actionId)
}

export function sameCampaignSubject(
  left: CampaignPlanSubject,
  right: CampaignPlanSubject
): boolean {
  return (
    left.kind === right.kind &&
    left.productId === right.productId &&
    (left.kind !== "existing-campaign" ||
      (right.kind === "existing-campaign" && left.campaignId === right.campaignId))
  )
}

export function safeHttpsUrl(value: string | null): string | null {
  if (value === null) {
    return null
  }
  try {
    const parsed = new URL(value)
    return parsed.protocol === "https:" ? parsed.toString() : null
  } catch {
    return null
  }
}

function isActionable(decision: CampaignAnalysisDetail["decision"]) {
  return ["raise-bid", "lower-bid", "pause", "activate"].includes(decision)
}

function aiActionLabel(analysis: CampaignAnalysisDetail) {
  if (analysis.decision === "pause") {
    return "Deactivate campaign"
  }
  if (analysis.decision === "activate") {
    return "Activate campaign"
  }
  return analysis.recommendedBid === null
    ? "Set bid"
    : `Set bid to €${analysis.recommendedBid.toFixed(2)}`
}

export function manualStateActionLabel(analysis: CampaignAnalysisDetail) {
  return manualStateAction(analysis) === "pause" ? "Deactivate campaign" : "Activate campaign"
}

export function shouldShowManualStateAction(
  analysis: CampaignAnalysisDetail,
  recommendationExpired: boolean
): boolean {
  if (recommendationExpired) {
    return true
  }
  return analysis.decision !== manualStateAction(analysis)
}

function manualStateAction(analysis: CampaignAnalysisDetail): "pause" | "activate" {
  const isActive = analysis.isActive ?? analysis.decision !== "activate"
  return isActive ? "pause" : "activate"
}

function decisionText(decision: CampaignAnalysisDetail["decision"]) {
  if (decision === "pause") {
    return "Deactivate"
  }
  const text = decision.replaceAll("-", " ")
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`
}

function money(value: number | null) {
  return value === null ? "—" : `€${value.toFixed(2)}`
}

function number(value: number | null) {
  return value === null ? "—" : value.toLocaleString()
}

function ratio(value: number | null) {
  return value === null ? "—" : `${value.toFixed(2)}×`
}

function bidRecommendation(analysis: CampaignAnalysisDetail) {
  if (analysis.recommendedBid === null) {
    return analysis.currentBid === null ? null : `Current bid ${money(analysis.currentBid)}`
  }
  return analysis.currentBid === null
    ? `Recommended starting bid ${money(analysis.recommendedBid)}`
    : `Current bid ${money(analysis.currentBid)} → recommended ${money(analysis.recommendedBid)}`
}

const RECOMMENDATION_TITLES = {
  pause: "Deactivate campaign",
  activate: "Activate campaign",
  "raise-bid": "Raise bid",
  "lower-bid": "Lower bid",
  propose: "Propose new campaign",
  hold: "Hold current bid",
  watch: "Keep watching",
  "data-check": "Check campaign data",
  retest: "Retest campaign",
} satisfies Record<CampaignAnalysisDetail["decision"], string>

function recommendationTitle(decision: CampaignAnalysisDetail["decision"]) {
  return RECOMMENDATION_TITLES[decision]
}

function decisionTone(decision: CampaignAnalysisDetail["decision"]) {
  if (decision === "raise-bid" || decision === "activate") {
    return "success" as const
  }
  if (decision === "lower-bid" || decision === "watch") {
    return "warning" as const
  }
  if (decision === "pause") {
    return "destructive" as const
  }
  if (decision === "propose") {
    return "brand" as const
  }
  return "muted" as const
}

function stateText(active: boolean | null) {
  if (active === null) {
    return "—"
  }
  return active ? "Active" : "Inactive"
}

function stockText(soldOut: boolean | null) {
  if (soldOut === null) {
    return "—"
  }
  return soldOut ? "Sold out" : "Usable"
}

function comparisonText(analysis: CampaignAnalysisDetail) {
  const action = analysis.engineAction?.replaceAll("-", " ") ?? "no engine action"
  const bid = analysis.engineBid === null ? "" : ` at €${analysis.engineBid.toFixed(2)}`
  return `${analysis.gateResult}: ${action}${bid}`
}

function statusText(status: CampaignActionPlan["status"]) {
  return status.replaceAll("_", " ")
}
